import { useEffect, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { SingleSelect, usePermissions } from '@/modules/common';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { invalidateTeamMemberWithRelatedQueries } from '@/modules/common/utils/query-invalidation';
import { buildUserGroupSelection } from '@/modules/common/utils/user-group-options';
import { useUserGroupsList } from '@/modules/settings/hooks/member-access/use-user-groups';
import { inviteTeamMember } from '@/modules/team/api/team.api';
import { TeamMember } from '@/modules/team/types/team-member';

interface InviteTeamMemberModalProps {
  teamMember: TeamMember;
  onSuccess?: () => void;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

const FORM_FIELD_GROUP_ID = 'groupId';
const FORM_FIELD_EMAIL = 'email';

const GROUP_REQUIRED_MESSAGE = 'authenticated.team:invite.groupRequired';

interface InviteSchemaBuilderParams {
  requiresEmail: boolean;
  requiresGroup: boolean;
}

const createInviteSchema = ({ requiresEmail, requiresGroup }: InviteSchemaBuilderParams) =>
  z.object({
    // `.min(1)` alongside `required_error`: the former catches an empty string, the latter a
    // missing value. Without both, clearing the select would satisfy the schema.
    [FORM_FIELD_GROUP_ID]: requiresGroup
      ? z.string({ required_error: GROUP_REQUIRED_MESSAGE }).min(1, GROUP_REQUIRED_MESSAGE)
      : z.string().optional(),
    [FORM_FIELD_EMAIL]: requiresEmail
      ? z.string().min(1, 'common:error.required').email('common:error.emailInvalid')
      : z.string().optional(),
  });

type InviteFormData = z.infer<ReturnType<typeof createInviteSchema>>;

/**
 * Modal for inviting a team member with basic status to join Opsium.
 * Sends an invitation email with access credentials to the selected user group.
 */
export function InviteTeamMemberModal({
  teamMember,
  onSuccess,
  trigger,
  open,
  onOpen,
  onClose,
}: InviteTeamMemberModalProps) {
  const { t } = useTranslation(['authenticated.team', 'common']);
  const queryClient = useQueryClient();

  const { displayToastErrorFromApiWithFallback } = useApiError();
  const { hasPermission } = usePermissions();
  const isAccessAndPermissionsManageAllowed = hasPermission(
    OpsiumPolicy.AccessAndPermissionsManage
  );
  const requiresEmail = !teamMember.email;
  const requiresGroup = isAccessAndPermissionsManageAllowed;

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Determine the actual modal open state (external or internal)
  const actualModalOpen = open !== undefined ? open : isModalOpen;

  // Only fetched for users who may pick a group — GET /api/team/user-groups requires
  // opsium-user-permissions:READ and 403s for everyone else. Members invited by those users are
  // placed in the organization's default group by the backend.
  const {
    data: userGroups,
    isLoading: isLoadingGroups,
    isFetched: areGroupsFetched,
  } = useUserGroupsList({ enabled: actualModalOpen && requiresGroup });

  const { options: groupOptions, defaultGroupValue } = useMemo(
    () =>
      buildUserGroupSelection({
        groups: userGroups,
        formatDefaultLabel: name => t('authenticated.team:invite.defaultGroupOption', { name }),
      }),
    [userGroups, t]
  );

  // Once the groups have settled and none can be picked, requiring one would leave the confirm
  // button disabled with no way out. The invite still succeeds — the backend falls back to the
  // organization's default group — so the field stops being required and explains itself instead.
  const hasNoSelectableGroups = areGroupsFetched && groupOptions.length === 0;
  const requiresGroupSelection = requiresGroup && !hasNoSelectableGroups;

  const inviteSchema = useMemo(
    () => createInviteSchema({ requiresEmail, requiresGroup: requiresGroupSelection }),
    [requiresEmail, requiresGroupSelection]
  );

  const form = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    mode: 'onChange',
    defaultValues: {
      [FORM_FIELD_GROUP_ID]: undefined,
      [FORM_FIELD_EMAIL]: '',
    },
  });

  // Seeds the form once per opening, after the groups have settled, so the default group can be
  // preselected even though it arrives asynchronously.
  //
  // `isFetched` rather than `!isLoading`: it also flips on error, and it is already true when the
  // modal reopens inside the cache window — so the select renders preselected in the first frame
  // instead of flashing empty. The ref keeps a background refetch from overwriting a group the
  // user picked by hand, and makes this the single place the form is reset (the mutation and the
  // close handler used to reset separately, which never ran when the parent owned `open`).
  const seededForOpeningRef = useRef(false);

  useEffect(() => {
    if (!actualModalOpen) {
      seededForOpeningRef.current = false;
      return;
    }
    if (seededForOpeningRef.current) return;
    if (requiresGroup && !areGroupsFetched) return;

    seededForOpeningRef.current = true;
    form.reset({ [FORM_FIELD_EMAIL]: '', [FORM_FIELD_GROUP_ID]: defaultGroupValue });
  }, [actualModalOpen, requiresGroup, areGroupsFetched, defaultGroupValue, form]);

  const inviteTeamMemberMutation = useMutation({
    mutationFn: ({
      memberId,
      groupId,
      email,
    }: {
      memberId: string;
      groupId?: number;
      email?: string;
    }) => inviteTeamMember(memberId, groupId, email),
    onSuccess: async () => {
      await invalidateTeamMemberWithRelatedQueries(queryClient, teamMember.id);
      onSuccess?.();
    },
    onError: error =>
      displayToastErrorFromApiWithFallback(error, t('authenticated.team:invite.inviteError')),
  });

  const handleInvite = async (closeModal: () => void) => {
    const isValid = await form.trigger();
    if (!isValid) {
      return;
    }

    const formData = form.getValues();

    try {
      await inviteTeamMemberMutation.mutateAsync({
        memberId: teamMember.id,
        groupId: formData.groupId ? parseInt(formData.groupId, 10) : undefined,
        email: requiresEmail ? formData.email : undefined,
      });
      closeModal();
    } catch (error) {
      // Error handled by mutation, keep modal open for retry
    }
  };

  const isFormValid = form.formState.isValid;

  const messageContent = (
    <div className="space-y-4">
      <div>
        {requiresEmail ? (
          <Trans
            i18nKey="authenticated.team:invite.inviteTeamMemberMessageNoEmail"
            values={{ name: teamMember.name }}
            components={{ strong: <strong /> }}
          />
        ) : (
          <Trans
            i18nKey="authenticated.team:invite.inviteTeamMemberMessage"
            values={{ name: teamMember.name, email: teamMember.email }}
            components={{ strong: <strong /> }}
          />
        )}
      </div>
      <div className="space-y-2">
        <p className="font-semibold">{t('authenticated.team:invite.whatHappens')}</p>
        <ul className="ml-2 list-inside list-disc space-y-1">
          <li>{t('authenticated.team:invite.emailWillBeSent')}</li>
          <li>{t('authenticated.team:invite.memberWillReceiveCredentials')}</li>
          <li>{t('authenticated.team:invite.statusWillChange')}</li>
          <li>{t('authenticated.team:invite.accessWillBeGranted')}</li>
        </ul>
      </div>
    </div>
  );

  return (
    <ConfirmationModal
      trigger={trigger}
      title={t('authenticated.team:invite.inviteTeamMember')}
      message={messageContent}
      confirmText={
        inviteTeamMemberMutation.isPending
          ? t('authenticated.team:invite.inviting')
          : t('authenticated.team:invite.sendInvite')
      }
      confirmVariant="default"
      onConfirm={handleInvite}
      isLoading={inviteTeamMemberMutation.isPending || isLoadingGroups}
      disabled={!isFormValid}
      open={actualModalOpen}
      onOpen={onOpen || (() => setIsModalOpen(true))}
      onClose={
        onClose ||
        (() => {
          setIsModalOpen(false);
        })
      }
    >
      <Form {...form}>
        <form className="space-y-4">
          {requiresEmail && (
            <FormField
              control={form.control}
              name={FORM_FIELD_EMAIL}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('authenticated.team:invite.emailAddress')}</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          {requiresGroup && (
            <SingleSelect
              name={FORM_FIELD_GROUP_ID}
              control={form.control}
              label={t('authenticated.team:invite.memberGroup')}
              placeholder={t('authenticated.team:invite.selectGroup')}
              options={groupOptions}
              searchable={false}
              loading={isLoadingGroups}
              noOptionsMessage={t('authenticated.team:invite.noGroupsAvailable')}
              isClearSelectionEnabled={false}
              warning={
                hasNoSelectableGroups ? t('authenticated.team:invite.noGroupsFallback') : undefined
              }
            />
          )}
        </form>
      </Form>
    </ConfirmationModal>
  );
}
